/**
 * client/src/briefs/api.ts — the shelf's network layer.
 *
 * It reuses `crm/api.ts`'s `request()` and `ApiError` rather than declaring a second fetch wrapper.
 * Both live under the same `/api/next/crm` prefix, use the same httponly session cookie, and
 * return errors in the same shape, so a second copy would be a second place for the
 * `credentials: 'same-origin'` rule to be got wrong. `crm/api.ts` imports only its own types, so
 * this does not pull the lazily-loaded CRM screens into the briefs chunk.
 *
 * TWO FLAGS, NOT ONE. `GET /status` answers `{enabled, reports_enabled}` and they are independent:
 * the shelf ships on while `VALUBRIEF_CRM_NEXT_ENABLED` stays off pending the cutover ADR-0004
 * leaves unscheduled. A caller that reads only `enabled` would hide a list that answers.
 */

import { ApiError, request } from '../crm/api';
import { apiFetch } from '../lib/auth';
import type { BriefHistory, BriefPage, BriefRow, ReportLinkRow } from './types';

export { ApiError };

/** Where this module is mounted in the browser's URL space. Must match `App.tsx`. */
export const BRIEFS_BASE_PATH = '/briefs';

export interface ShelfStatus {
  /** The CRM module. Governs filing a brief against a parcel/engagement/party. */
  enabled: boolean;
  /** The shelf itself. Governs the list, the timeline and delete/restore. */
  reports_enabled: boolean;
  org_id: string | null;
}

export async function getShelfStatus(signal?: AbortSignal): Promise<ShelfStatus> {
  const body = await request<Partial<ShelfStatus>>('/status', { signal });
  return {
    enabled: body.enabled === true,
    // Defaulting to FALSE when the key is missing, even though the server defaults the flag to
    // on: a missing key means an older server that has no shelf, and showing a link to routes
    // that 404 is the dead end the nav bar's own rule exists to prevent.
    reports_enabled: body.reports_enabled === true,
    org_id: body.org_id ?? null,
  };
}

export interface ListBriefsOptions {
  kind?: string;
  propertyKey?: string;
  q?: string;
  /** Narrow to the caller's own work. A FILTER — the list is org-scoped by default, because a
   * colleague must be able to see a brief their own brokerage paid to produce. */
  mine?: boolean;
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}

export function listBriefs(options: ListBriefsOptions = {}): Promise<BriefPage> {
  const { signal, ...rest } = options;
  return request<BriefPage>('/reports', {
    signal,
    query: {
      kind: rest.kind,
      property_key: rest.propertyKey,
      q: rest.q,
      mine: rest.mine ? 'true' : undefined,
      include_deleted: rest.includeDeleted ? 'true' : undefined,
      limit: rest.limit,
      offset: rest.offset,
    },
  });
}

export function getBrief(reportId: string, signal?: AbortSignal): Promise<BriefRow> {
  return request<BriefRow>(`/reports/${encodeURIComponent(reportId)}`, { signal });
}

/**
 * One property's briefs, oldest first, with the movement between them.
 *
 * `propertyKey` is the ENGINE's normalised key and is passed back exactly as the list handed it
 * over. Normalising an address in the browser would split one property into two the moment the two
 * rules disagreed about a suffix, and the split would look like a real gap in the history.
 */
export function getPropertyHistory(
  propertyKey: string,
  options: { includeDeleted?: boolean; signal?: AbortSignal } = {},
): Promise<BriefHistory> {
  return request<BriefHistory>('/reports/history', {
    signal: options.signal,
    query: {
      property_key: propertyKey,
      include_deleted: options.includeDeleted ? 'true' : undefined,
    },
  });
}

/** Rename. The only field on a shelf row a user owns — a figure a user could type is a figure
 * with no source. `null` clears it and the list falls back to the report's own address. */
export function renameBrief(reportId: string, title: string | null): Promise<BriefRow> {
  return request<BriefRow>(`/reports/${encodeURIComponent(reportId)}`, {
    method: 'PATCH',
    body: { title },
  });
}

/**
 * Remove a brief from every list. SOFT — the report itself stays at its URL and stays
 * downloadable, because it may already have been handed to a client and cited in a hearing.
 * Returns the row, so the caller can offer an undo without a second round trip.
 */
export function deleteBrief(reportId: string): Promise<BriefRow> {
  return request<BriefRow>(`/reports/${encodeURIComponent(reportId)}`, { method: 'DELETE' });
}

/**
 * ================================================================================================
 * DUPLICATE A BRIEF (F1, 2026-09-21)
 * ================================================================================================
 * `apiFetch` RATHER THAN THIS MODULE'S `request`, and the difference is the prefix. `request` is
 * the CRM shelf's, mounted under `/api/next/crm`; a duplicate is an act on the JOB, so it lives
 * beside `/editable` and `/result` under `/api/next/amb` where the payload actually is. Routing it
 * through the shelf would mean the shelf copying a brief it deliberately does not hold — it stores
 * a POINTER, and `report_sync.py` says so at length.
 *
 * TAKES `source_id`, NOT THE SHELF ROW'S `id`. They are different identifiers for different things
 * and mixing them is the sort of thing that 404s in production and passes in a test with one row.
 */
export interface DuplicatedBrief {
  jobId: string;
  duplicatedFrom: string;
  status: string;
  address: string | null;
  /** Where to open the copy. Composed by the server so a route change does not need two edits. */
  open: string;
}

export function duplicateBrief(sourceId: string, signal?: AbortSignal): Promise<DuplicatedBrief> {
  return apiFetch<DuplicatedBrief>(
    `/api/next/amb/jobs/${encodeURIComponent(sourceId)}/duplicate`,
    { method: 'POST', signal },
  );
}

/** The undo. Idempotent by design — an undo that can fail is not an undo. */
export function restoreBrief(reportId: string): Promise<BriefRow> {
  return request<BriefRow>(`/reports/${encodeURIComponent(reportId)}/restore`, { method: 'POST' });
}

export function listBriefLinks(
  reportId: string,
  signal?: AbortSignal,
): Promise<{ items: ReportLinkRow[]; count: number }> {
  return request(`/reports/${encodeURIComponent(reportId)}/links`, { signal });
}

/** File a brief against a parcel, engagement or party. Needs the CRM flag — a filing screen with
 * nothing to file against is not a feature, which is why this route sits behind the other flag. */
export function fileBrief(
  reportId: string,
  subject: { subject_type: 'parcel' | 'engagement' | 'party'; subject_id: string; note?: string },
): Promise<ReportLinkRow> {
  return request<ReportLinkRow>(`/reports/${encodeURIComponent(reportId)}/links`, {
    method: 'POST',
    body: subject,
  });
}
